// =============================================================================
// PTEL - Auth Context
// =============================================================================

import type {
  ReactNode} from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from 'react';
import type {
  User as FirebaseUser} from 'firebase/auth';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db, COLLECTIONS, isConfigured } from '../config/firebase';
import { checkAndEnforceExpiration } from '../services/firebase/roleExpiration.service';
import { logSessionAction, logRoleExpiration } from '../services/firebase/audit.service';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

// Importado y re-exportado desde roles.ts para mantener compatibilidad con imports existentes
import type { UserRole } from '@/types/roles';
export type { UserRole } from '@/types/roles';

/** Constante para detectar error de email no verificado en LoginForm */
export const EMAIL_NOT_VERIFIED_ERROR = 'Verifica tu correo antes de iniciar sesión.';

export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  emailVerified: boolean;
  createdAt?: Date;
  lastLogin?: Date;
  roleExpiresAt?: Date | null;
}

export interface AuthContextValue {
  user: FirebaseUser | null;
  userData: UserData | null;
  loading: boolean;
  error: string | null;
  isConfigured: boolean;
  networkBlocked: boolean;
  /** B20: true cuando la sesión caducó en esta pestaña (user pasó de autenticado a null) */
  sessionExpired: boolean;
  /**
   * HIST: true cuando `user` es el muñeco del bypass de desarrollo y NO una
   * sesión real de Firebase. Su `getIdToken` devuelve un marcador que el Worker
   * rechaza (403), así que todo consumidor que vaya a autenticarse contra el
   * Worker debe consultar esta bandera antes de intentarlo.
   * Siempre `false` en producción: el bypass exige `import.meta.env.DEV`.
   */
  isDevBypass: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: (email: string, password: string) => Promise<void>;
  isAdmin: boolean;
  isEditor: boolean;
  clearError: () => void;
  roleExpiresAt: Date | null;
  isRoleExpiringSoon: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// -----------------------------------------------------------------------------
// Error Messages
// -----------------------------------------------------------------------------

/** SEC0-b2b DOP-19: avisos del registro cuando la cuenta ya existe pero algo falló después. */
export const REGISTER_VERIFY_NOT_SENT_ERROR =
  'Tu cuenta está creada, pero no hemos podido enviarte el correo de verificación. ' +
  'Por favor, pide que te lo reenviemos desde la pantalla de acceso.';
export const REGISTER_PROFILE_NOT_SAVED_ERROR =
  'Tu cuenta está creada, pero no hemos podido guardar tus datos de usuario. ' +
  'Por favor, escríbenos a contacto@pteltool.es para que lo resolvamos.';
/** SEC0-b2b DOP-17 (a): restablecer cuando el Worker no contesta (no depende de la cuenta). */
export const RESET_UNAVAILABLE_ERROR =
  'No hemos podido enviar el correo ahora. Por favor, inténtalo más tarde.';

const getErrorMessage = (errorCode: string): string => {
  const messages: Record<string, string> = {
    'auth/email-already-in-use': 'Este correo ya está registrado.',
    'auth/invalid-email': 'Correo electrónico no válido.',
    'auth/missing-email': 'Escribe tu correo electrónico.',
    'auth/missing-password': 'Escribe tu contraseña.',
    'auth/weak-password': 'Contraseña muy débil (mínimo 6 caracteres).',
    'auth/user-not-found': 'No existe cuenta con este correo.',
    'auth/wrong-password': 'Contraseña incorrecta.',
    'auth/invalid-credential': 'Credenciales inválidas.',
    'auth/user-disabled': 'Esta cuenta está desactivada.',
    'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos.',
    'auth/network-request-failed': 'No hay conexión. Comprueba tu red e inténtalo de nuevo.',
    // SEC0-b2b DOP-5 (a): respuestas del Worker de correo (geodata.pteltool.es)
    'worker/400': 'Correo electrónico no válido.',
    'worker/401': 'Tu sesión no es válida. Por favor, vuelve a iniciar sesión.',
    'worker/429': 'Has pedido demasiados correos. Por favor, espera una hora y vuelve a intentarlo.',
    'worker/502': 'No hemos podido enviar el correo. Por favor, inténtalo más tarde.',
    'worker/503': 'El servicio de correo no está disponible ahora. Por favor, inténtalo más tarde.',
    'worker/network': 'No hemos podido conectar con el servicio de correo. Comprueba tu red e inténtalo de nuevo.',
  };
  if (messages[errorCode]) return messages[errorCode];
  if (errorCode.startsWith('worker/')) return 'No hemos podido enviar el correo. Por favor, inténtalo más tarde.';
  return 'Error de autenticación.';
};

/** Error de un punto de correo del Worker: `code` = `worker/<estado>` o `worker/network`. */
class WorkerEmailError extends Error {
  readonly code: string;
  /** Texto propio del Worker (solo se enseña en el 429, que lo trae pensado para la persona). */
  readonly workerMessage?: string;
  constructor(code: string, workerMessage?: string, cause?: unknown) {
    super(workerMessage ?? code, { cause });
    this.name = 'WorkerEmailError';
    this.code = code;
    this.workerMessage = workerMessage;
  }
}

/** Mensaje para la persona: el 429 lleva su propio texto; el resto, por código. */
const toMessage = (err: unknown): string => {
  const e = (err ?? {}) as { code?: string; workerMessage?: string; message?: string };
  if (e.code === 'worker/429' && e.workerMessage) return e.workerMessage;
  if (e.code) return getErrorMessage(e.code);
  return e.message || 'Error de autenticación.';
};

/**
 * SEC0-b2b DOP-9/10/12: único camino de los correos de cuenta. Llama a
 * `WORKER_BASE` fijo (no a `VITE_WORKER_URL`, DOP-12 b) y, si hay sesión,
 * manda `Authorization: Bearer <idToken>`. Sin segunda vía en el navegador:
 * si el Worker no envía, lanza `WorkerEmailError` con el motivo real.
 */
async function postWorkerEmail(
  endpoint: 'send-verify-email' | 'send-reset-email',
  body: Record<string, unknown>,
  idToken?: string,
): Promise<{ alreadyVerified?: boolean }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  let res: Response;
  try {
    // Escrita así para que el guardián de `r2Inventory` vea la ruta `api`.
    res = await fetch(`${WORKER_BASE}/api/${endpoint}`, { method: 'POST', headers, body: JSON.stringify(body) });
  } catch (cause) {
    throw new WorkerEmailError('worker/network', undefined, cause);
  }
  const data = (await res.json().catch(() => ({}))) as { error?: string; alreadyVerified?: boolean };
  if (!res.ok) {
    throw new WorkerEmailError(`worker/${res.status}`, data.error);
  }
  return data;
}

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [networkBlocked, setNetworkBlocked] = useState(false);
  // B20: detectar caducidad de sesión en pestaña activa
  const [sessionExpired, setSessionExpired] = useState(false);
  const hadUserRef = useRef(false);
  const [roleExpiresAt, setRoleExpiresAt] = useState<Date | null>(null);
  const [isRoleExpiringSoon, setIsRoleExpiringSoon] = useState(false);

  // HIST: la misma puerta dual que gobierna el bypass, evaluada en el cuerpo del
  // componente para que sea legible desde el valor del contexto. NO se sube a
  // ámbito de módulo: allí se congelaría en tiempo de importación y `vi.stubEnv`
  // dejaría de alcanzarla (medido en la sonda de HIST, 21-ago).
  const isDevBypass =
    import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

  // Auth State Listener
  useEffect(() => {
    // DEV BYPASS: VITE_BYPASS_AUTH=true simula un usuario editor sin Firebase.
    // Gate DEV obligatorio: en producción Vite sustituye import.meta.env.DEV
    // por `false` literal y el bloque entero se elimina por dead-code elimination.
    // Sin este gate, definir VITE_BYPASS_AUTH=true en Cloudflare Pages abriría
    // el wizard sin autenticación en pteltool.es (TD-013, Hallazgo D).
    const BYPASS_AUTH =
      import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';
    if (BYPASS_AUTH) {
      console.warn('[PTEL Auth] Bypass activo SOLO en DEV local. NUNCA en producción.');
      // HIST: el muñeco debe exponer `getIdToken`. Cuatro consumidores lo llaman
      // (HistorialSection, EditHistoryPanel, CoordConfirmButton, WizardPageV2) y
      // sin él reventaban con «user.getIdToken is not a function». El marcador
      // que devuelve NO autentica: el Worker lo rechaza con 403. Para saber si
      // hay sesión real, léase `isDevBypass` del contexto, no este valor.
      // Sincronizar las mismas claves que escribe el login real (véase `login`):
      // HomePage gobierna la zona de arrastre por localStorage + fecha de hoy,
      // no por `user`. Sin esto, cada día nuevo el muñeco abría el modal de login
      // y el login real fallaba contra el emulador de Auth vacío (CATAMB, 11-sep).
      localStorage.setItem('ptel_authenticated', 'true');
      localStorage.setItem('ptel_auth_date', new Date().toISOString().split('T')[0]);
      localStorage.setItem('ptel_user', 'Dev Local');
      setUser({
        uid: 'dev-bypass',
        email: 'dev@local.ptel',
        getIdToken: async () => 'dev-bypass-token',
      } as unknown as FirebaseUser);
      setUserData({ uid: 'dev-bypass', email: 'dev@local.ptel', displayName: 'Dev Local', role: 'editor', emailVerified: true });
      setLoading(false);
      return;
    }

    if (!isConfigured || !auth) {
      setLoading(false);
      return;
    }

    // Timeout de seguridad: si Firebase Auth no responde en 8 s, la red está
    // probablemente bloqueada (adblocker, firewall corporativo, sin conexión).
    // En lugar de colgar en pantalla negra, mostramos un mensaje claro.
    const AUTH_TIMEOUT_MS = 8000;
    const authTimeout = setTimeout(() => {
      console.warn('[PTEL Auth] Firebase Auth no respondió en', AUTH_TIMEOUT_MS, 'ms — posible red bloqueada');
      setNetworkBlocked(true);
      setLoading(false);
    }, AUTH_TIMEOUT_MS);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Firebase Auth respondió — cancelar el timeout de seguridad
      clearTimeout(authTimeout);
      setUser(firebaseUser);

      // B20: detectar transición autenticado → null (sesión caducada en pestaña activa)
      if (firebaseUser) {
        hadUserRef.current = true;
        setSessionExpired(false);
      } else if (hadUserRef.current) {
        // Había sesión activa y ahora es null → caducó
        setSessionExpired(true);
      }

      if (firebaseUser && db) {
        try {
          const userDocRef = doc(db, COLLECTIONS.USERS, firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: data.displayName || '',
              role: data.role as UserRole,
              emailVerified: firebaseUser.emailVerified,
            });
            // lastLogin se actualiza en login(), no aquí, para evitar
            // permission-denied en onAuthStateChanged durante el registro.
            // --- FB.10.06: Comprobar expiración del rol ---
            const firestoreExpiresAt = data.roleExpiresAt;
            const expiresAtDate = firestoreExpiresAt?.toDate
              ? (firestoreExpiresAt.toDate() as Date)
              : firestoreExpiresAt instanceof Date
                ? firestoreExpiresAt
                : null;

            const expirationResult = await checkAndEnforceExpiration({
              uid: firebaseUser.uid,
              role: data.role as UserRole,
              roleExpiresAt: expiresAtDate,
            });

            if (expirationResult.wasDowngraded) {
              setUserData(prev => prev ? { ...prev, role: 'viewer' } : prev);
              // FB.08: auditar caducidad de rol — fire-and-forget, no bloquea flujo
              void logRoleExpiration(
                firebaseUser.uid,
                firebaseUser.email ?? '',
                data.role as string,
              ).catch(() => { /* auditoría no bloquea */ });
            }

            setRoleExpiresAt(expirationResult.remaining.expiresAt);
            setIsRoleExpiringSoon(expirationResult.remaining.expiringSoon);
          } else {
            // Doc no existe todavía — ocurre durante el registro porque onAuthStateChanged
            // se dispara antes de que register() escriba el documento en Firestore.
            // No auto-crear aquí: register() es responsable de crear el doc.
            // Limpiamos userData explícitamente para que los componentes que observan
            // userData (p.ej. el panel de admin) no intenten operar sin rol definido.
            setUserData(null);
            setRoleExpiresAt(null);
            setIsRoleExpiringSoon(false);
            // En desarrollo: warn visible; en producción: debug (no visible por defecto)
            if (import.meta.env.DEV) {
              console.warn('[PTEL Auth] User doc missing for', firebaseUser.uid, '— waiting for register()')
            } else {
              console.debug('[PTEL Auth] User doc missing for', firebaseUser.uid, '— waiting for register()')
            };
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
      } else {
        setUserData(null);
        setRoleExpiresAt(null);
        setIsRoleExpiringSoon(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Login
  const login = useCallback(async (email: string, password: string) => {
    if (!auth) {
      const msg = 'Firebase no configurado';
      setError(msg);
      throw new Error(msg);
    }
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (!result.user.emailVerified) {
        await signOut(auth);
        throw new Error(EMAIL_NOT_VERIFIED_ERROR);
      }
      // Audit: registrar inicio de sesión
      void logSessionAction('login', result.user.uid, result.user.email ?? email);
      // Sincronizar estado en localStorage para que HomePage muestre la dropzone
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('ptel_authenticated', 'true');
      localStorage.setItem('ptel_auth_date', today);
      localStorage.setItem('ptel_user', result.user.displayName ?? result.user.email ?? '');
    } catch (err: unknown) {
      const message = (err as { code?: string }).code 
        ? getErrorMessage((err as { code: string }).code) 
        : (err as Error).message;
      setError(message);
      throw new Error(message, { cause: err });
    }
  }, []);

  // Register
  const register = useCallback(async (email: string, password: string, displayName: string) => {
    if (!auth || !db) {
      const msg = 'Firebase no configurado';
      setError(msg);
      throw new Error(msg);
    }
    setError(null);
    let accountCreated = false;
    // SEC0-b2b DOP-16/19: si la cuenta se crea pero la ficha o el correo
    // fallan, se dice (no «¡Registro completado!»). La ficha manda sobre el correo.
    let postCreateError: string | null = null;
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      accountCreated = true;

      const firstNameOnly = displayName.split(' ')[0];
      await updateProfile(result.user, { displayName: firstNameOnly });

      console.log('[PTEL Register] STEP 1: getIdToken');
      const idToken = await result.user.getIdToken(true);

      // `setDoc` resuelve cuando el servidor acusa la escritura: su rechazo
      // es la señal de que la ficha `users/{uid}` no existe.
      console.log('[PTEL Register] STEP 2: setDoc users');
      try {
        await setDoc(doc(db, COLLECTIONS.USERS, result.user.uid), {
          email,
          displayName,
          role: 'viewer' as UserRole,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        });
      } catch (err) {
        console.error('[PTEL Register] La ficha users/{uid} no se guardó:', (err as Error).message);
        postCreateError = REGISTER_PROFILE_NOT_SAVED_ERROR;
      }

      // Verificación: solo por el Worker con la sesión, antes de cerrarla
      // (DOP-9/10). Sin segunda vía nativa.
      console.log('[PTEL Register] STEP 3: send-verify-email (Worker)');
      try {
        await postWorkerEmail('send-verify-email', {}, idToken);
      } catch (err) {
        console.warn('[PTEL Register] La verificación no salió:', (err as { code?: string }).code);
        postCreateError ??= REGISTER_VERIFY_NOT_SENT_ERROR;
      }
    } catch (err: unknown) {
      if (!accountCreated) {
        console.error('[PTEL Register] Error al crear cuenta:', (err as { code?: string }).code, (err as Error).message);
        const message = toMessage(err);
        setError(message);
        throw new Error(message, { cause: err });
      }
      // Fallo tras crear la cuenta y antes de escribir la ficha
      // (`updateProfile`, `getIdToken`): no hay ficha ni correo.
      console.error('[PTEL Register] Error en paso post-creación:', (err as Error).message);
      postCreateError = REGISTER_PROFILE_NOT_SAVED_ERROR;
    } finally {
      // Siempre cerrar sesión al terminar el registro,
      // independientemente de si hubo errores en pasos secundarios.
      console.log('[PTEL Register] STEP 4: signOut');
      if (auth.currentUser) {
        await signOut(auth).catch(() => {/* signOut silencioso */});
      }
    }
    if (postCreateError) {
      setError(postCreateError);
      throw new Error(postCreateError);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    if (!auth) return;
    // Audit: registrar cierre de sesión antes de signOut
    const currentUser = auth.currentUser;
    if (currentUser) {
      void logSessionAction('logout', currentUser.uid, currentUser.email ?? '');
    }
    await signOut(auth);
  }, []);

  // Reset Password — solo por el Worker (Resend + noreply@pteltool.es), DOP-10 (a) ampliada.
  // El Worker responde lo mismo exista o no la cuenta; si no contesta, DOP-17 (a).
  const resetPassword = useCallback(async (email: string) => {
    if (!auth) {
      const msg = 'Firebase no configurado';
      setError(msg);
      throw new Error(msg);
    }
    setError(null);
    try {
      await postWorkerEmail('send-reset-email', { email });
    } catch (err: unknown) {
      // 400 = correo mal formado; cualquier otro fallo (red, 5xx) no depende
      // de la cuenta y no revela si existe.
      const code = (err as { code?: string }).code;
      const message = code === 'worker/400' ? getErrorMessage(code) : RESET_UNAVAILABLE_ERROR;
      setError(message);
      throw new Error(message, { cause: err });
    }
  }, []);

  // Resend Verification Email (FB.06)
  const resendVerification = useCallback(async (email: string, password: string) => {
    if (!auth) {
      const msg = 'Firebase no configurado';
      setError(msg);
      throw new Error(msg);
    }
    setError(null);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user.emailVerified) {
        // Ya está verificado — no hacer signOut, dejar logueado
        return;
      }
      // Reenviar solo por el Worker con la sesión (DOP-9/10); la sesión se
      // cierra pase lo que pase: una cuenta sin verificar no se queda dentro.
      try {
        const idToken = await result.user.getIdToken();
        await postWorkerEmail('send-verify-email', {}, idToken);
      } finally {
        await signOut(auth).catch(() => {/* signOut silencioso */});
      }
    } catch (err: unknown) {
      const message = toMessage(err);
      setError(message);
      throw new Error(message, { cause: err });
    }
  }, []);

  // Permission Helpers
  const isAdmin = userData?.role === 'admin';
  const isEditor = userData?.role === 'editor' || isAdmin;

  const clearError = useCallback(() => setError(null), []);

  // Context Value
  const value: AuthContextValue = {
    user,
    userData,
    loading,
    error,
    isConfigured,
    networkBlocked,
    sessionExpired,
    isDevBypass,
    login,
    register,
    logout,
    resetPassword,
    resendVerification,
    isAdmin,
    isEditor,
    clearError,
    roleExpiresAt,
    isRoleExpiringSoon,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}

export { AuthContext };
